<template>
  <component
    :is="tag_seccion"
    :class="merge_clases(usar_estilos_legacy ? 'lag_legacy' : '', wrapperClass)"
    :style="estilo_final_seccion"
    :aria-label="ariaLabel || null"
  >
    <!-- Título -->
    <div
      v-if="debe_mostrar_titulo"
      :class="merge_clases('lag_legacy__header', headerColClass)"
      :style="headerColStyle"
    >
      <slot name="titulo">
        <div :class="merge_clases('lag_legacy__title_wrap', titleWrapClass)" :style="titleWrapStyle">
          <img
            v-if="titleIconSrc"
            :src="titleIconSrc"
            :alt="titleIconAlt"
            :width="titleIconWidth"
            :height="titleIconHeight"
            :class="merge_clases('lag_legacy__title_icon', titleIconClass)"
            :style="titleIconStyle"
            loading="lazy"
            decoding="async"
          />

          <component
            :is="titleTag"
            :class="merge_clases('lag_legacy__title', titleClass)"
            :style="titleStyle"
          >
            {{ title }}
          </component>
        </div>
      </slot>
    </div>

    <!-- Lista -->
    <component
      :is="listTag"
      :class="merge_clases(usar_estilos_legacy ? 'lag_legacy__grid' : '', listClass)"
      :style="listStyle"
    >
      <component
        :is="itemTag"
        v-for="(item, index) in items_normalizados"
        :key="item.key ?? index"
        :class="merge_clases(
          usar_estilos_legacy ? 'lag_legacy__item' : '',
          itemsColClass,
          item.col_class
        )"
        :style="merge_estilos(itemsColStyle, item.col_style)"
      >
        <slot name="item" :item="item" :index="index">
          <div
            :class="merge_clases(
              usar_estilos_legacy ? 'lag_legacy__row' : '',
              itemRowClass,
              item.row_class
            )"
            :style="merge_estilos(itemRowStyle, item.row_style)"
          >
            <slot name="icono" :item="item" :index="index">
              <img
                v-if="item.icon_src"
                :src="item.icon_src"
                :alt="item.icon_alt ?? ''"
                :class="merge_clases(
                  usar_estilos_legacy ? 'lag_legacy__icon' : '',
                  iconClass,
                  item.icon_class
                )"
                :style="merge_estilos(iconStyle, item.icon_style)"
                :loading="imgLoading"
                :decoding="imgDecoding"
              />
            </slot>

            <component
              :is="textTag"
              :class="merge_clases(
                usar_estilos_legacy ? 'lag_legacy__text' : '',
                textClass,
                item.text_class
              )"
              :style="merge_estilos(textStyle, item.text_style)"
            >
              {{ item.texto }}
            </component>
          </div>
        </slot>
      </component>
    </component>
  </component>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import type { CSSProperties, PropType } from 'vue';

type ClassObject = Record<string, boolean>;
type VueClass = string | ClassObject | VueClass[];

export interface PasoServicioItem {
  key?: string | number;
  texto: string;

  icon_src?: string;
  icon_alt?: string;

  col_class?: VueClass;
  row_class?: VueClass;
  icon_class?: VueClass;
  text_class?: VueClass;

  col_style?: CSSProperties;
  row_style?: CSSProperties;
  icon_style?: CSSProperties;
  text_style?: CSSProperties;
}

const slots = useSlots();

const props = defineProps({
  // Activa layout legacy (por defecto sí).
  usarEstilosLegacy: { type: Boolean, default: true },

  // Contenido
  items: { type: Array as PropType<PasoServicioItem[]>, default: () => [] },

  // Wrapper
  ariaLabel: { type: String, default: '' },
  as: { type: String, default: '' },
  wrapperTag: { type: String, default: 'div' },
  wrapperClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  wrapperStyle: { type: Object as PropType<CSSProperties>, default: undefined },

  // Título
  mostrarTitulo: { type: Boolean, default: true },
  title: { type: String, default: '' },
  titleTag: { type: String, default: 'h2' },
  headerColClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  headerColStyle: { type: Object as PropType<CSSProperties>, default: undefined },
  titleWrapClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  titleWrapStyle: { type: Object as PropType<CSSProperties>, default: undefined },
  titleClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  titleStyle: { type: Object as PropType<CSSProperties>, default: undefined },

  titleIconSrc: { type: String, default: '' },
  titleIconAlt: { type: String, default: '' },
  titleIconWidth: { type: [Number, String], default: 20 },
  titleIconHeight: { type: [Number, String], default: 26 },
  titleIconClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  titleIconStyle: { type: Object as PropType<CSSProperties>, default: undefined },

  // Lista
  listTag: { type: String, default: 'div' },
  itemTag: { type: String, default: 'div' },
  listClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  listStyle: { type: Object as PropType<CSSProperties>, default: undefined },

  // Estilos globales “extra” (si el padre quiere sumar clases/estilos)
  itemsColClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  itemsColStyle: { type: Object as PropType<CSSProperties>, default: undefined },
  itemRowClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  itemRowStyle: { type: Object as PropType<CSSProperties>, default: undefined },
  iconClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  iconStyle: { type: Object as PropType<CSSProperties>, default: undefined },
  textClass: { type: [String, Array, Object] as PropType<VueClass>, default: '' },
  textStyle: { type: Object as PropType<CSSProperties>, default: undefined },
  textTag: { type: String, default: 'p' },

  // UX/Performance para imgs
  imgLoading: { type: String as PropType<'lazy' | 'eager'>, default: 'lazy' },
  imgDecoding: { type: String as PropType<'async' | 'sync' | 'auto'>, default: 'async' },

  // ✅ Variables legacy configurables desde el padre (sin tocar CSS global)
  legacyPaddingY: { type: String, default: '70px' },
  legacyPaddingX: { type: String, default: '190px' },
  legacyPaddingYMobile: { type: String, default: '30px' },
  legacyPaddingXMobile: { type: String, default: '20px' },
  legacyPaddingXMid: { type: String, default: '130px' }, // 1200–1800

  legacyItemPaddingY: { type: String, default: '10px' },
  legacyItemPaddingX: { type: String, default: '90px' },
  legacyItemPaddingXMid: { type: String, default: '50px' },
  legacyItemPaddingMobile: { type: String, default: '10px' },

  legacyIconSize: { type: String, default: '72px' },
  legacyTextGap: { type: String, default: '10px' },
  legacyTitleMarginBottom: { type: String, default: '40px' },
});

const usar_estilos_legacy = computed(() => props.usarEstilosLegacy);
const tag_seccion = computed(() => props.as || props.wrapperTag);

const debe_mostrar_titulo = computed(() => {
  if (!props.mostrarTitulo) return false;
  if (slots.titulo) return true;
  return Boolean(props.title);
});

const items_normalizados = computed(() => (Array.isArray(props.items) ? props.items : []));

const legacy_vars = computed<CSSProperties | undefined>(() => {
  if (!usar_estilos_legacy.value) return undefined;

  return {
    // Sección
    '--lag-py': props.legacyPaddingY,
    '--lag-px': props.legacyPaddingX,
    '--lag-py-m': props.legacyPaddingYMobile,
    '--lag-px-m': props.legacyPaddingXMobile,
    '--lag-px-mid': props.legacyPaddingXMid,

    // Item
    '--lag-item-py': props.legacyItemPaddingY,
    '--lag-item-px': props.legacyItemPaddingX,
    '--lag-item-px-mid': props.legacyItemPaddingXMid,
    '--lag-item-p-m': props.legacyItemPaddingMobile,

    // UI
    '--lag-icon': props.legacyIconSize,
    '--lag-gap': props.legacyTextGap,
    '--lag-title-mb': props.legacyTitleMarginBottom,
  } as CSSProperties;
});

const estilo_final_seccion = computed<CSSProperties | undefined>(() => {
  // wrapperStyle del padre puede sobreescribir variables si quiere
  return merge_estilos(legacy_vars.value, props.wrapperStyle);
});

function merge_clases(...clases: Array<VueClass | undefined | null | false>): VueClass | '' {
  const resultado: Array<string | ClassObject> = [];

  const push = (valor: VueClass | undefined | null | false) => {
    if (!valor) return;
    if (Array.isArray(valor)) {
      for (const v of valor) push(v);
      return;
    }
    resultado.push(valor);
  };

  for (const c of clases) push(c);

  if (resultado.length === 0) return '';
  if (resultado.length === 1) return resultado[0];
  return resultado as VueClass;
}

function merge_estilos(base?: CSSProperties, extra?: CSSProperties): CSSProperties | undefined {
  if (!base && !extra) return undefined;
  return { ...(base ?? {}), ...(extra ?? {}) };
}
</script>

<style scoped>
/* Sección: paddings legacy con variables */
.lag_legacy {
  padding: var(--lag-py) var(--lag-px);
}

@media (max-width: 800px) {
  .lag_legacy {
    padding: var(--lag-py-m) var(--lag-px-m);
  }
}

@media (min-width: 1200px) and (max-width: 1800px) {
  .lag_legacy {
    padding: var(--lag-py) var(--lag-px-mid);
  }
}

.lag_legacy__header {
  width: 100%;
  margin-bottom: var(--lag-title-mb);
}

.lag_legacy__title_wrap {
  display: flex;
  justify-content: center;
  align-items: center;
}

.lag_legacy__title_icon {
  margin-top: 5px;
  margin-right: 5px;
}

.lag_legacy__title {
  text-align: center;
  font-weight: 700;
}

/* Grid 1 col / 2 col como Bootstrap md */
.lag_legacy__grid {
  display: grid;
  grid-template-columns: 1fr;
}

@media (min-width: 768px) {
  .lag_legacy__grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* Item padding */
.lag_legacy__item {
  padding: var(--lag-item-py) var(--lag-item-px);
}

@media (max-width: 800px) {
  .lag_legacy__item {
    padding: var(--lag-item-p-m);
  }
}

@media (min-width: 1200px) and (max-width: 1800px) {
  .lag_legacy__item {
    padding: var(--lag-item-py) var(--lag-item-px-mid);
  }
}

/* Row */
.lag_legacy__row {
  display: flex;
  align-items: center;
}

/* Icon + texto */
.lag_legacy__icon {
  width: var(--lag-icon);
  height: var(--lag-icon);
  object-fit: contain;
  display: block;
}

.lag_legacy__text {
  margin: 0;
  margin-left: var(--lag-gap);
}
</style>
